/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190703 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { EmployeeDaoService } from '../employee-dao.service';
import { Employee } from 'src/app/_model/employee';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { AppConfig } from 'src/app/app.config';
import { KeyValuePersistenceService } from 'src/app/_services/persistence/key-value-persistence/key-value-persistence.service';
import { IMSObject } from 'src/app/_model/ims-object';
import { EmployeeDbTableDefinition } from './employee-db-table-definition';
import { TeamEmployeeDbTableDefinition } from './team-employee-db-table-definition';

// CONSTANTS
const ME_KEY = 'employee_me';

@Injectable()
export class EmployeeDBDaoService implements EmployeeDaoService {
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private keyValuePersistenceServie: KeyValuePersistenceService,
    private employeeTableDefinition: EmployeeDbTableDefinition,
    private teamEmployeeTableDefinition: TeamEmployeeDbTableDefinition,
  ) {}

  public saveMe(employee: Employee): Promise<void> {
    return this.keyValuePersistenceServie.store(ME_KEY, employee);
  }

  public async saveEmployees(employees: Employee[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var empQueries: Query[] = [];
      for (const employee of employees) {
        let empQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            this.employeeTableDefinition.tableName +
            '(id, label, databag, name, lastname, imsappstatus, ' +
            ' primarycarid, primarycarlabel, ' +
            ' employmenttypeid, employmenttypelabel,' +
            ' tag,  appusername, assignedsvempid, assignedsvemplabel)' +
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?);',
          parameters: [
            Number(employee.getImsId()),
            employee.label,
            employee.databag ? JSON.stringify(employee.databag) : undefined,
            employee.name ? employee.name : '',
            employee.lastname ? employee.lastname : '',
            employee.imsappstatus ? employee.imsappstatus : '',
            employee.primarycar ? Number(employee.primarycar.getImsId()) : null,
            employee.primarycar
              ? employee.primarycar.label
                ? employee.primarycar.label
                : ''
              : null,
            employee.employmenttype
              ? Number(employee.employmenttype.getImsId())
              : null,
            employee.employmenttype
              ? employee.employmenttype.label
                ? employee.employmenttype.label
                : ''
              : null,
            employee.tag ? employee.tag : '',
            employee.appusername ? employee.appusername : '',
            employee.assignedSvEmp
              ? Number(employee.assignedSvEmp.getImsId())
              : null,
            employee.assignedSvEmp
              ? employee.assignedSvEmp.label
                ? employee.assignedSvEmp.label
                : ''
              : null,
          ],
        };
        empQueries.push(empQuery);
      }

      await this.dbPersistenceService.executeStatements(db, empQueries);
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        'Error saving employees to db',
        error?.message,
      );
    }
  }

  public async saveTeam(employees: Employee[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var empQueries: Query[] = [];
      for (const employee of employees) {
        let empQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            this.teamEmployeeTableDefinition.tableName +
            '(id, label, databag, name, lastname, imsappstatus, ' +
            ' primarycarid, primarycarlabel, ' +
            ' employmenttypeid, employmenttypelabel,' +
            ' tag,  appusername, assignedsvempid, assignedsvemplabel)' +
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?);',
          parameters: [
            Number(employee.getImsId()),
            employee.label,
            employee.databag ? JSON.stringify(employee.databag) : undefined,
            employee.name ? employee.name : '',
            employee.lastname ? employee.lastname : '',
            employee.imsappstatus ? employee.imsappstatus : '',
            employee.primarycar ? Number(employee.primarycar.getImsId()) : null,
            employee.primarycar
              ? employee.primarycar.label
                ? employee.primarycar.label
                : ''
              : null,
            employee.employmenttype
              ? Number(employee.employmenttype.getImsId())
              : null,
            employee.employmenttype
              ? employee.employmenttype.label
                ? employee.employmenttype.label
                : ''
              : null,
            employee.tag ? employee.tag : '',
            employee.appusername ? employee.appusername : '',
            employee.assignedSvEmp
              ? Number(employee.assignedSvEmp.getImsId())
              : null,
            employee.assignedSvEmp
              ? employee.assignedSvEmp.label
                ? employee.assignedSvEmp.label
                : ''
              : null,
          ],
        };
        empQueries.push(empQuery);
      }

      await this.dbPersistenceService.executeStatements(db, empQueries);
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        'Error saving team to db',
        error?.message,
      );
    }
  }

  public async getAllEmployees(): Promise<Employee[]> {
    try {
      let employees: Employee[] = await this.getEmployees();
      let team: Employee[] = await this.getTeam();
      let me: Employee[] = [await this.getMe()];

      return me.concat(employees, team);
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        'Error getting all employees:',
        error?.message,
      );
      return undefined;
    }
  }

  public async getMe(): Promise<Employee> {
    let employees = await this.getEmployees();
    let team = await this.getTeam();
    let me = (await this.keyValuePersistenceServie.load(ME_KEY)) as Employee;

    let meEmp = team?.find((temp) => temp.id.imsId == me?.id?.imsId);
    if (meEmp) {
      return meEmp;
    }

    meEmp = employees?.find((emp) => emp.id.imsId == me?.id?.imsId);

    return meEmp;
  }

  public async getEmployees(): Promise<Employee[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let employees: Employee[] = [];

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.employeeTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        employees.push(
          Employee.fromDto(
            {
              id: row['id'],
              secondaryid: undefined,
              label: row['label'],
              name: row['name'],
              imsappstatus: row['imsappstatus'],
              primarycar: {
                id: row['primarycarid'],
                label: row['primarycarlabel'],
              },
              employmenttype: {
                id: row['employmenttypeid'],
                label: row['employmenttypelabel'],
              },
              tag: row['tag'],
              lastname: row['lastname'],
              appusername: row['appusername'],
              assignedsvemp: {
                id: row['assignedsvempid'],
                label: row['assignedsvemplabel'],
              },
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }

      return employees;
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        'Error reading employees from db',
        error?.message,
      );
      return undefined;
    }
  }

  public async getTeam(): Promise<Employee[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let employees: Employee[] = [];

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.teamEmployeeTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        employees.push(
          Employee.fromDto(
            {
              id: row['id'],
              secondaryid: undefined,
              label: row['label'],
              name: row['name'],
              imsappstatus: row['imsappstatus'],
              primarycar: {
                id: row['primarycarid'],
                label: row['primarycarlabel'],
              },
              employmenttype: {
                id: row['employmenttypeid'],
                label: row['employmenttypelabel'],
              },
              tag: row['tag'],
              lastname: row['lastname'],
              appusername: row['appusername'],
              assignedsvemp: {
                id: row['assignedsvempid'],
                label: row['assignedsvemplabel'],
              },
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }

      return employees;
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        'Error reading team from db',
        error?.message,
      );
      return undefined;
    }
  }

  public async addToTeam(employee: Employee): Promise<void> {
    try {
      await this.moveEmployeeFromTableToTable(
        employee,
        this.employeeTableDefinition.tableName,
        this.teamEmployeeTableDefinition.tableName,
      );
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        'Error adding employees to team',
        error?.message,
      );
    }
  }
  public async removeFromTeam(employee: Employee): Promise<void> {
    try {
      await this.moveEmployeeFromTableToTable(
        employee,
        this.teamEmployeeTableDefinition.tableName,
        this.employeeTableDefinition.tableName,
      );
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        'Error removing employees from team',
        error?.message,
      );
    }
  }

  // Resetable
  public async reset() {
    // Do nothing
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.employeeTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.employeeTableDefinition.tableName,
    );
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.teamEmployeeTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.teamEmployeeTableDefinition.tableName,
    );
    await this.keyValuePersistenceServie.delete(ME_KEY);
  }

  // Private Functions
  private async moveEmployeeFromTableToTable(
    employee: Employee,
    fromTable: string,
    toTable: string,
  ) {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      this.dbPersistenceService.executeBatch(db, [
        {
          statement: 'DELETE FROM ' + fromTable + ' WHERE id=?',
          parameters: [employee.getImsId()],
        },
        {
          statement:
            'INSERT OR REPLACE INTO ' +
            toTable +
            '(id, label, databag, name, lastname, imsappstatus, ' +
            ' primarycarid, primarycarlabel, ' +
            ' employmenttypeid, employmenttypelabel,' +
            ' tag,  appusername, assignedsvempid, assignedsvemplabel)' +
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
          parameters: [
            Number(employee.getImsId()),
            employee.label,
            employee.databag ? JSON.stringify(employee.databag) : undefined,
            employee.name ? employee.name : '',
            employee.lastname ? employee.lastname : '',
            employee.imsappstatus ? employee.imsappstatus : '',
            employee.primarycar ? Number(employee.primarycar.getImsId()) : null,
            employee.primarycar
              ? employee.primarycar.label
                ? employee.primarycar.label
                : ''
              : null,
            employee.employmenttype
              ? Number(employee.employmenttype.getImsId())
              : null,
            employee.employmenttype
              ? employee.employmenttype.label
                ? employee.employmenttype.label
                : ''
              : null,
            employee.tag ? employee.tag : '',
            employee.appusername ? employee.appusername : '',
            employee.assignedSvEmp
              ? Number(employee.assignedSvEmp.getImsId())
              : null,
            employee.assignedSvEmp
              ? employee.assignedSvEmp.label
                ? employee.assignedSvEmp.label
                : ''
              : null,
          ],
        },
      ]);
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        'Error mobing employee',
        employee.label,
        'from',
        fromTable,
        'to',
        toTable,
        ': ',
        error?.message,
      );
      throw error;
    }
  }
}
